/**
 * MobileStackNav - Mobile Bottom Navigation
 *
 * Gesture-aware mobile navigation with smooth animations and large touch targets
 */
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useDragControls, AnimatePresence } from 'framer-motion';
import { useNavigationStack } from '../../contexts/NavigationContext';
import { useAuth } from '../../contexts/AuthContext';
import { searchFashion } from '../../api/rakuten_service';
import './MobileStackNav.css';

const MobileStackNav = () => {
  const location = useLocation();
  const { goBackWithAnimation, canGoBack, navigateWithTransition } = useNavigationStack();
  const { currentUser } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showCameraSheet, setShowCameraSheet] = useState(false);

  // Handle scroll to show/hide navigation
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // Show nav when scrolling up or at top
      if (currentScrollY < lastScrollY || currentScrollY < 50) {
        setIsVisible(true);
      }
      // Hide nav when scrolling down
      else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
      }

      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const navItems = [
    {
      path: '/',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      ),
      label: 'Home'
    },
    {
      path: '/deals',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
          <line x1="7" y1="7" x2="7.01" y2="7" />
        </svg>
      ),
      label: 'Deals',
      requiresAuth: true
    },
    {
      path: 'camera',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
          <circle cx="12" cy="13" r="4"/>
        </svg>
      ),
      label: 'Camera',
      isCamera: true
    },
    {
      path: '/favorites',
      icon: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
        </svg>
      ),
      label: 'Favorites',
      requiresAuth: true
    },
    {
      path: '/profile',
      icon: currentUser?.photoURL ? (
        <img
          src={currentUser.photoURL}
          alt="Profile"
          className="mobile-nav-profile-pic"
        />
      ) : (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      ),
      label: 'Profile',
      requiresAuth: false
    }
  ];

  const handleCameraClick = () => {
    // Show bottom sheet with camera/gallery options
    setShowCameraSheet(true);
  };

  // Handle selecting camera option
  const handleCameraOption = () => {
    setShowCameraSheet(false);
    const cameraInput = document.getElementById('mobile-camera-input');
    if (cameraInput) {
      cameraInput.click();
    }
  };

  // Handle selecting gallery option
  const handleGalleryOption = () => {
    setShowCameraSheet(false);
    const galleryInput = document.getElementById('mobile-gallery-input');
    if (galleryInput) {
      galleryInput.click();
    }
  };

  // Handle camera capture - process the image and navigate to results
  const handleCameraCapture = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const imagePreview = URL.createObjectURL(file);
    console.log('📷 Camera capture received:', file.name);

    // Navigate to analyzing page first with smooth transition
    navigateWithTransition('/analyzing', {
      direction: 'forward',
      state: { image: imagePreview }
    });

    try {
      const results = await searchFashion(imagePreview);
      navigateWithTransition('/results', {
        direction: 'forward',
        state: { image: imagePreview, results }
      });
    } catch (error) {
      console.error('Camera search failed:', error);
      alert(error.message);
      navigateWithTransition('/', { direction: 'back' });
    }

    // Reset the input so the same file can be selected again
    event.target.value = '';
  };

  const handleNavClick = (item) => {
    if (item.isCamera) {
      handleCameraClick();
    } else if (item.requiresAuth && !currentUser) {
      navigateWithTransition('/auth', {
        direction: 'modal',
        state: { from: location.pathname }
      });
    } else {
      navigateWithTransition(item.path, { direction: 'forward' });
    }
  };

  // Swipe back gesture handler
  const handleDragEnd = (event, info) => {
    setIsDragging(false);

    // Swipe right with sufficient velocity triggers back
    if (info.offset.x > 100 && info.velocity.x > 500 && canGoBack) {
      goBackWithAnimation();
    }
  };

  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Swipe back gesture area - only on pages where back is available */}
      {canGoBack && location.pathname !== '/' && (
        <motion.div
          className="swipe-back-area"
          drag="x"
          dragConstraints={{ left: 0, right: 300 }}
          dragElastic={0.2}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={handleDragEnd}
          style={{
            opacity: isDragging ? 0.3 : 0,
            pointerEvents: isDragging ? 'auto' : 'none'
          }}
        >
          <div className="swipe-back-indicator">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            <span>Swipe to go back</span>
          </div>
        </motion.div>
      )}

      {/* Mobile Bottom Navigation */}
      <motion.nav
        className="mobile-stack-nav"
        initial={{ y: 100 }}
        animate={{ y: isVisible ? 0 : 100 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        <div className="mobile-nav-content">
          {navItems.map((item, index) => (
            <motion.button
              key={item.path}
              className={`mobile-nav-item ${isActive(item.path) ? 'active' : ''} ${item.isCamera ? 'camera-btn' : ''}`}
              onClick={() => handleNavClick(item)}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="mobile-nav-icon">
                {item.icon}
              </div>
              <span className="mobile-nav-label">{item.label}</span>
            </motion.button>
          ))}
        </div>
      </motion.nav>

      {/* Hidden camera input for mobile nav */}
      <input
        type="file"
        id="mobile-camera-input"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleCameraCapture}
      />

      {/* Hidden gallery input for mobile nav (no capture attribute) */}
      <input
        type="file"
        id="mobile-gallery-input"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleCameraCapture}
      />

      {/* Camera/Gallery Bottom Sheet */}
      <AnimatePresence>
        {showCameraSheet && (
          <>
            {/* Backdrop */}
            <motion.div
              className="camera-sheet-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowCameraSheet(false)}
            />

            {/* Bottom Sheet */}
            <motion.div
              className="camera-sheet"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <div className="camera-sheet-handle" />
              <h3 className="camera-sheet-title">Add Photo</h3>

              <div className="camera-sheet-options">
                <button className="camera-sheet-option" onClick={handleCameraOption}>
                  <div className="camera-sheet-icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </div>
                  <div className="camera-sheet-option-text">
                    <span className="camera-sheet-option-title">Take Photo</span>
                    <span className="camera-sheet-option-subtitle">Use your camera</span>
                  </div>
                </button>

                <button className="camera-sheet-option" onClick={handleGalleryOption}>
                  <div className="camera-sheet-icon gallery">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                      <circle cx="8.5" cy="8.5" r="1.5"></circle>
                      <polyline points="21 15 16 10 5 21"></polyline>
                    </svg>
                  </div>
                  <div className="camera-sheet-option-text">
                    <span className="camera-sheet-option-title">Choose from Gallery</span>
                    <span className="camera-sheet-option-subtitle">Select an existing photo</span>
                  </div>
                </button>
              </div>

              <button className="camera-sheet-cancel" onClick={() => setShowCameraSheet(false)}>
                Cancel
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default MobileStackNav;

